import React, { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { tradersApi } from 'api';
import {
  CommissionInfo,
  CopyText,
  CrudPage,
  DataGridColumnDefinition,
  IntervalCommissionsInfoDialog,
  MailLink,
  StatusLabel,
  UserInfo,
} from 'components';
import {
  UserRole,
  FilterDefinitionType,
  QueryKey,
  TraderWorkStatus,
} from 'enums';
import { usePartialQuery } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { FilterDefinition, IntervalCommission, Trader } from 'types';
import { traderUtils } from 'utils';

export const OperatorTradersPage: React.FC = () => {
  const { t } = useTranslation(TranslationNamespace.Operator, {
    keyPrefix: 'pages.traders',
  });
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);

  const queryResult = usePartialQuery(
    QueryKey.Traders,
    tradersApi.getAllPaginatedAsRole(UserRole.Operator),
  );

  const [
    intervalCommissionsInfoDialogProps,
    setIntervalCommissionsInfoDialogOpen,
  ] = useState<{
    open: boolean;
    intervalCommissions?: IntervalCommission[];
  }>({ open: false });

  const columns = useMemo(
    (): DataGridColumnDefinition<Trader>[] => [
      {
        header: t('fields.id'),
        valueGetter: (item) => <CopyText text={item.id} truncateLength={10} />,
      },
      {
        header: t('fields.name'),
        valueGetter: (item) => (
          <Fragment>
            <UserInfo name={item.user?.name} id={item.user?.id} />
            {item.user?.email && <MailLink email={item.user?.email} />}
          </Fragment>
        ),
      },
      {
        header: t('fields.compensations'),
        valueGetter: (item) => (
          <CommissionInfo
            commission={item.compensation}
            payoutCommission={item.payoutCompensation}
            intervalCommissions={item.intervalCommissions}
            onIntervalCommissionsButtonClick={(e: React.SyntheticEvent) => {
              e.stopPropagation();
              setIntervalCommissionsInfoDialogOpen({
                open: true,
                intervalCommissions: item.intervalCommissions,
              });
            }}
          />
        ),
      },
      {
        header: t('fields.work_status'),
        valueGetter: (item) => (
          <StatusLabel
            label={traderUtils.getWorkStatusLabel(item.workStatus)}
            status={item.workStatus}
          />
        ),
      },
    ],
    [t],
  );

  const filtersDefinitions: FilterDefinition<Trader>[] = [
    {
      label: t('filters.work_status'),
      name: 'workStatus',
      type: FilterDefinitionType.Enum,
      getDisplayName: traderUtils.getWorkStatusLabel,
      enum: TraderWorkStatus,
    },
    {
      label: `${t('filters.compensation')} (${tCommon('common.payin')})`,
      type: FilterDefinitionType.Numeric,
      name: 'compensation',
    },
    {
      label: `${t('filters.compensation')} (${tCommon('common.payout')})`,
      type: FilterDefinitionType.Numeric,
      name: 'payoutCompensation',
    },
  ];

  return (
    <Fragment>
      <CrudPage
        header={{ title: t('title') }}
        filters={{ filtersDefinitions }}
        table={{ queryResult, paginated: true, columns }}
      />
      <IntervalCommissionsInfoDialog
        {...intervalCommissionsInfoDialogProps}
        onClose={() => setIntervalCommissionsInfoDialogOpen({ open: false })}
      />
    </Fragment>
  );
};
